/**
 * @flow
 */

import { Field, Form, Formik } from "formik"
import React, { useState } from "react"
import styled from "styled-components"
import * as yup from "yup"

import { validatePhoneNumber } from "../../utils/phonenumber"
import { TITLE_OPTIONS } from "../../utils/titles"
import {
  Button,
  EmailField,
  PasswordField,
  SelectField,
  TextField,
  CheckBoxField,
} from "../elements"

const PASSWORD_HELP_TEXT =
  "At least 8 characters, including an uppercase and lowercase letter, a number and a special character."

const validationSchema = yup.object().shape({
  title: yup.string().required("Required"),
  firstName: yup.string().required("Required"),
  lastName: yup.string().required("Required"),
  email: yup
    .string()
    .email("Invalid email address")
    .required("Required"),
  mobileNumber: yup
    .string()
    .required("Required")
    .test("phone-number", "Invalid phone number", value =>
      validatePhoneNumber(value)
    ),
  password: yup.string().required("Required"),
})

type Values = {
  title: string,
  firstName: string,
  lastName: string,
  email: string,
  mobileNumber: string,
  password: string,
}

type Actions = {
  setSubmitting: Function,
}

type Props = {
  onSubmit: (values: Values, actions: Actions) => void | Promise<mixed>,
  initialValues?: Values,
}

const DisclaimerText = styled.p`
  font-size: 1rem;
  line-height: 1.25rem;
  margin-bottom: 0.75rem;

  strong {
    font-family: "galano_grotesquemedium";
    font-weight: 400;
  }
`

const List = styled.ul`
  padding-left: 1rem;
  margin-bottom: 0.75rem;

  li {
    margin-bottom: 0.25rem;
  }
`

const SignUpForm = ({
  onSubmit,
  initialValues = {
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    password: "",
  },
}: Props) => {
  const [terms, setTerms] = useState(false)
  const toggleTerms = () => {
    setTerms(!terms)
  }
  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur={false}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <Form noValidate>
          <Field
            autoFocus
            component={SelectField}
            id="title"
            label="Title"
            name="title"
            options={TITLE_OPTIONS}
            required
          />
          <Field
            autoComplete="given-name"
            component={TextField}
            id="firstName"
            label="First Name"
            name="firstName"
            required
          />
          <Field
            autoComplete="family-name"
            component={TextField}
            id="lastName"
            label="Last Name"
            name="lastName"
            required
          />
          <Field
            autoComplete="email"
            component={EmailField}
            id="email"
            label="Email Address"
            name="email"
            placeholder="someone@example.com"
            required
          />
          <Field
            autoComplete="tel"
            component={TextField}
            id="mobileNumber"
            label="Phone Number"
            name="mobileNumber"
            required
          />
          <Field
            autoComplete="current-password"
            component={PasswordField}
            id="password"
            label="Password"
            name="password"
            helpText={PASSWORD_HELP_TEXT}
            required
          />

          <DisclaimerText>
            <strong>BEFORE YOU AGREE - PLEASE READ</strong>
          </DisclaimerText>

          <DisclaimerText>
            These Terms contain provisions that limit Submitt&apos;s liability
            to you, that require you to accept certain risks, and that require
            you to indemnify Submitt. Your attention is drawn to them in terms
            of section 49 of the Consumer Protection Act 68 of 2008. They are
            clauses 3.6, 12, 13.1 to 13.4 and 13.8. In summary:
          </DisclaimerText>

          <List>
            <li>
              You must check every claim, code and modifier before you submit
              it. Submitt does not check them for you and is not responsible if
              they are wrong.
            </li>
            <li>
              You are responsible for everything done through your account,
              including by anyone who uses your login.
            </li>
            <li>
              You must have obtained whatever consent is required before
              processing your patients&apos; information through Submitt.
            </li>
            <li>
              Submitt&apos;s liability to you is limited to the subscription
              fees you paid in the preceding 12 months, and does not cover lost
              income, lost profits or reputational harm.
            </li>
            <li>
              You indemnify Submitt against claims arising from your use of the
              app, including claims by patients and medical schemes.
            </li>
          </List>

          <DisclaimerText>
            Please read the full Terms and the Privacy Policy before you agree.
          </DisclaimerText>

          <CheckBoxField onSelect={toggleTerms} active={terms} />
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting || !terms}
          >
            Create Account
          </Button>
        </Form>
      )}
    </Formik>
  )
}

export default SignUpForm
